import { Booking } from '@pages/booking/types';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isFutureCalendarItem } from '@pages/booking/utils';
import { Alert, CircularProgress } from '@mui/material';
import { getDatesDifferenceFromDateRange } from '@shared/utils/utils';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '@features/auth/store/selectors';

interface BookingCredentialsPlaceholderProps {
    bookingForItem: Booking;
    isInProgress: boolean;
}

const BookingCredentialsPlaceholder: FC<BookingCredentialsPlaceholderProps> = ({ bookingForItem, isInProgress }) => {
    const { t } = useTranslation();
    const currentUser = useSelector(selectCurrentUser);
    const isBookingInFuture = bookingForItem !== null && isFutureCalendarItem(bookingForItem.booked_from);
    const isOwner = bookingForItem?.created_by_user === currentUser;

    const bookingWillBeAvailableAfter = useMemo(() => {
        if (!(isBookingInFuture && bookingForItem && isOwner)) {
            return;
        }

        return getDatesDifferenceFromDateRange(new Date(), bookingForItem.booked_from);
    }, [bookingForItem, isBookingInFuture, isOwner]);

    return (
        <>
            {bookingWillBeAvailableAfter ? (
                <Alert sx={{ mb: 1, mt: 2 }} severity="info">
                    {t('booking_credentials_hint_message')
                        .concat(
                            bookingWillBeAvailableAfter[0]
                                ? ` ${t('days', { count: bookingWillBeAvailableAfter[0] })}`
                                : '',
                        )
                        .concat(
                            bookingWillBeAvailableAfter[1]
                                ? ` ${t('hours', { count: bookingWillBeAvailableAfter[1] })}`
                                : '',
                        )
                        .concat(
                            bookingWillBeAvailableAfter[2]
                                ? ` ${t('minutes', { count: bookingWillBeAvailableAfter[2] + 1 })}`
                                : '',
                        )}
                </Alert>
            ) : (
                isInProgress &&
                isOwner && (
                    <Alert sx={{ mb: 1 }} severity="info" icon={<CircularProgress size="20px" />}>
                        {t('booking_credentials_almost_ready')}
                    </Alert>
                )
            )}
        </>
    );
};

export default BookingCredentialsPlaceholder;
